"use client";

import clsx from "clsx";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface IntegrationTileProps {
  testId: string;
  /** The dialog's Close button — kept separate so each tile keeps its own id. */
  closeTestId: string;
  /** Rendered inside the rounded square in the header. */
  icon: ReactNode;
  /** Background for that square — each integration brings its own brand colour. */
  iconClassName: string;
  title: string;
  /** Drives the Connected badge only. The footer label is `actionLabel`. */
  connected: boolean;
  /** The card body: a pitch, a count, a loading line or an error line. */
  description: ReactNode;
  /** Already resolved by the caller — "Manage" once there is something to manage. */
  actionLabel: string;
  actionDisabled?: boolean;
  dialogTitle: string;
  dialogDescription: string;
  /** The Manage dialog's body — the integration's setup panel. */
  children: ReactNode;
}

/**
 * Card chrome for the built-in marketplace integrations. The dialog's only
 * footer button is Close, not Save: every setup panel section commits on its own
 * button, so there is nothing pending here to confirm or discard.
 */
export default function IntegrationTile({
  testId,
  closeTestId,
  icon,
  iconClassName,
  title,
  connected,
  description,
  actionLabel,
  actionDisabled = false,
  dialogTitle,
  dialogDescription,
  children,
}: Readonly<IntegrationTileProps>) {
  const { t: ct } = useTranslation("common");
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Card
        className={clsx(
          "relative",
          "flex",
          "flex-col",
          "shadow-none",
          "border-2",
          "border-gray-300",
          "dark:border-gray-700",
          "w-full",
          "min-w-sm"
        )}
        data-testid={testId}
      >
        <CardHeader className={clsx("flex", "flex-col", "gap-2")}>
          <div className="flex items-center gap-3">
            <div
              className={clsx(
                "flex items-center justify-center w-10 h-10 rounded-lg overflow-hidden",
                iconClassName
              )}
            >
              {icon}
            </div>
            <div className="flex flex-col flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">{title}</h3>
                {connected && (
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-600 dark:text-green-400 dark:border-green-400"
                  >
                    {ct("connected")}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className={clsx("flex", "flex-col", "flex-1")}>
          {description}
        </CardContent>
        <CardFooter>
          <Button
            onClick={() => setDialogOpen(true)}
            disabled={actionDisabled}
            className="w-full"
          >
            {actionLabel}
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>
          {children}
          <DialogFooter>
            <Button onClick={() => setDialogOpen(false)} data-testid={closeTestId}>
              {ct("close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
